"use client"

import { CardBasic } from "@/components/ui-library-new/card/CardBasic"
import { IconCircle } from "@/components/ui-library-new/icon/IconCircle"
import { Button } from "@/components/ui-library/button/Button"
import { Chip } from "@/components/ui-library/chip/Chip"
import { withVaccineFilter } from "@/consts/query-params"
import { stripConfigSuffix } from "@/consts/vaccination-slugs"
import { trackEvent } from "@/lib/amplitude/amplitude"
import { VACCINATION_CTA_EVENTS } from "@/lib/amplitude/vaccinationCtaEvents"
import { urlParser } from "@/lib/urlParser"
import { VaccineWithCampaign } from "@/storyblok/vaccinationRelations"
import { cn } from "@/utils/utils"
import { campaignDiscount, isCampaignActive } from "@/utils/vaccineCampaign"
import { numericPrice } from "@/utils/vaccineLabels"
import { ISbStoryData } from "@storyblok/react/rsc"
import Image from "next/image"
import Link from "next/link"
import { usePathname } from "next/navigation"

export type VaccineStory = ISbStoryData<VaccineWithCampaign>

/**
 * A price, struck through and followed by the campaign price while one runs.
 *
 * "Gratis" rather than "0 kr" — a free vaccine is a different fact, not a price of zero — and a free
 * price is never discounted, which would otherwise render a negative amount under a struck "0 kr".
 * The vaccine detail table says the same things in `VaccineTypeInfoCardSection`; the two are read
 * side by side on the same page, so they must not disagree.
 */
const CardPrice = ({ value, discount }: { value: string | undefined; discount?: string }) => {
  const price = numericPrice(value)

  // A range like "490-1150" is a price, just not one to do arithmetic on. Shown as written, and no
  // discount is struck from it — there is no single number to subtract from.
  if (price === null) return <>{value?.trim() ? `${value} kr` : "Gratis"}</>
  if (price <= 0) return <>Gratis</>

  const amount = numericPrice(discount)
  if (!amount) return <>{`${price} kr`}</>

  return (
    <>
      <span className="text-neutral-30 line-through">{`${price} kr`}</span>{" "}
      <span>{`${Math.max(price - amount, 0)} kr`}</span>
    </>
  )
}

// Both card actions are styled identically — one source so they can't drift apart.
const actionButtonClasses =
  "py-1 text-sm font-semibold tracking-[-0.045em] 2xl:px-6 2xl:py-2 2xl:text-[17px] 2xl:leading-[23px]"

export const VaccinationPriceCard = ({
  story,
  showBookAction = true
}: {
  story: VaccineStory
  showBookAction?: boolean
}) => {
  // Client component only because of the CTA tracking: the card is rendered on three different
  // pages, so the page it was clicked from travels in `page_path` rather than in the event name.
  const pathname = usePathname()

  const {
    name,
    icon,
    show_required_amount,
    required_amount,
    markers_amount,
    show_markers,
    description,
    price_adult,
    price_child,
    adult_only,
    campaign_image,
    read_more_link,
    book_link
  } = story.content

  const vaccineSlug = stripConfigSuffix(story.full_slug).split("/").filter(Boolean).pop() ?? ""

  // One gate for the badge and both prices: a campaign runs only while it is switched on, has
  // something to say and has not passed its end date.
  const discount = campaignDiscount(story.content)

  return (
    <CardBasic className="min-h-[172px] justify-center p-4 2xl:min-h-[280px] 2xl:justify-between 2xl:p-8">
      <div className="mb-3 flex items-center gap-3 py-0 text-sm font-semibold 2xl:mb-2 2xl:gap-4 2xl:py-2">
        {/* Icon */}
        <IconCircle
          icon={icon}
          className="h-10 w-10 lg:h-14 lg:w-14 2xl:h-[70px] 2xl:w-[70px]"
          iconClassName="h-5 w-5 lg:h-7 lg:w-7 2xl:h-8 2xl:w-8"
        />

        {/* Name & chips */}
        <div className="flex min-w-0 flex-col items-start gap-1 2xl:gap-2">
          <p className="text-[15px] font-semibold text-neutral-90 2xl:text-xl">{name}</p>

          <div className="flex flex-wrap gap-1 2xl:gap-2">
            {show_required_amount === true && required_amount && (
              <Chip variant="info" overrideElement="span">
                {required_amount}
              </Chip>
            )}
            {show_markers === true && (
              <Chip variant="info" overrideElement="span">
                {markers_amount > 1 ? `${markers_amount} markörer` : `${markers_amount} markör`}
              </Chip>
            )}
          </div>
        </div>

        {/* svg */}
        {isCampaignActive(story.content) && campaign_image?.filename && (
          <div className="ml-auto inline-flex shrink-0 items-center gap-2 self-start">
            <Image
              src={campaign_image.filename}
              alt={campaign_image.alt || ""}
              width={90}
              height={90}
              sizes="90px"
              className="h-16 w-16 object-contain xl:h-20 xl:w-20 2xl:h-24 2xl:w-24"
            />
          </div>
        )}
      </div>
      {/* description */}
      <p className="mb-6 text-sm tracking-normal text-neutral-50 2xl:mb-10 2xl:text-base">{description}</p>
      {/* row for prices, and read more button and book time button*/}
      <div
        className={cn("flex items-center justify-between gap-4 tracking-normal", {
          "items-end": adult_only
        })}
      >
        <div className="flex shrink-0 flex-col">
          {Number(price_adult) === 0 && Number(price_child) === 0 ? (
            <span className="whitespace-nowrap text-lg font-bold leading-tight text-neutral-90 2xl:text-2xl">
              Gratis
            </span>
          ) : (
            <>
              <span className="whitespace-nowrap text-lg font-bold leading-tight text-neutral-90 2xl:text-2xl">
                {discount ? (
                  <div className="flex flex-col gap-1">
                    <CardPrice value={price_adult} discount={discount} />
                  </div>
                ) : (
                  <CardPrice value={price_adult} />
                )}
              </span>
              {!adult_only && (
                <span className="whitespace-nowrap text-xs font-normal tracking-wide text-neutral-90 2xl:text-[14px]">
                  {/* The struck price is the FULL one — `campaign_price` holds the discount, so
                      striking that would show the reader 300 kr crossed out. */}
                  Barn: <CardPrice value={price_child} discount={discount} />
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {read_more_link?.cached_url && (
            <Button asChild size="small" variant="neutral" className={actionButtonClasses}>
              <Link href={urlParser(read_more_link.cached_url)}>Läs mer</Link>
            </Button>
          )}
          {showBookAction && book_link?.cached_url && (
            <Button asChild size="small" variant="primary" className={actionButtonClasses}>
              {/* The chosen vaccine travels in the URL, so the clinic list can pre-filter on it and
                  the result stays shareable, bookmarkable and back-button friendly. */}
              <Link
                onClick={() =>
                  trackEvent(VACCINATION_CTA_EVENTS.vaccine_card_book_cta_pressed, { page_path: pathname })
                }
                href={withVaccineFilter(urlParser(book_link.cached_url), vaccineSlug)}
              >
                Boka tid
              </Link>
            </Button>
          )}
        </div>
      </div>
    </CardBasic>
  )
}
